import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';

import { shareReplay } from 'rxjs/operators';
import { KPI } from '../api';
import { ConfigService } from './config.service';
import { AvailableLanguage, LanguageService } from './language.service';

import { STATE_LIMITS } from './constants';
import { firstLetterUpperCase } from './utils.service';

/** Raw value of the ASSET__TECHNICAL_STATES setting (any supported format). */
type TechnicalStatesRaw = Record<string, string | Record<string, string>>;

@Injectable()
export class StateLevelsService {
  public levels$: Observable<number[]> = of(
    <number[]>Object.values(KPI.NotationLevel).filter(Number.isInteger),
  ).pipe(shareReplay(1));

  constructor(
    private translate: TranslateService,
    private config: ConfigService,
    private languageService: LanguageService,
  ) { }

  private readonly LANG_CODES: AvailableLanguage[] = ['fr', 'en', 'es', 'pl'];

  /**
   * Returns the level index (0-3) for a given notation.
   * 0: <= 22 (Critical)
   * 1: <= 46 (Deteriorated)
   * 2: <= 70 (Acceptable)
   * 3: > 70 (Good / New)
   */
  public getLevelIndexFromNotation(notation: number): number | null {
    if (notation === null || notation === undefined) return null;
    if (notation <= STATE_LIMITS[1]) return 0; // <= 22
    if (notation <= STATE_LIMITS[2]) return 1; // <= 46
    if (notation <= STATE_LIMITS[3]) return 2; // <= 70
    return 3; // 71-100 (Good + New)
  }

  /**
   * Resolves the raw ASSET__TECHNICAL_STATES setting into a flat key→label map
   * for the current language. Handles three formats:
   *   - Language-first (new):  { fr: { critical: "critique" }, en: { critical: "critical" } }
   *   - Key-first (old):       { critical: { fr: "critique", en: "critical" } }
   *   - Legacy flat:           { critical: "critique" }
   */
  private getLangDict(states: TechnicalStatesRaw): Record<string, string> {
    const lang = this.languageService.getCurrentLanguage() ?? 'fr';
    const firstValue = Object.values(states)[0];

    if (typeof firstValue === 'string') {
      // Legacy flat: { critical: "critique", ... }
      return states as Record<string, string>;
    }

    if (Object.keys(states).some(k => this.LANG_CODES.includes(k as AvailableLanguage))) {
      // New language-first: { fr: { critical: "critique" }, en: { ... } }
      return (states[lang] ?? states['fr'] ?? Object.values(states).find(v => typeof v === 'object') ?? {}) as Record<string, string>;
    }

    // Old key-first: { critical: { fr: "critique", en: "critical" } }
    const result: Record<string, string> = {};
    for (const [key, entry] of Object.entries(states)) {
      result[key] = typeof entry === 'string'
        ? entry
        : (entry[lang] ?? entry['fr'] ?? Object.values(entry)[0] ?? '');
    }
    return result;
  }

  /**
   * Returns the label for a given state key, using ASSET__TECHNICAL_STATES setting
   * when available, falling back to i18n translations.
   */
  public getStateLabel(key: string): string {
    const states = this.config.getSettingValue<TechnicalStatesRaw>('ASSET__TECHNICAL_STATES', {});
    if (states && Object.keys(states).length > 0) {
      const label = this.getLangDict(states)[key];
      if (label) return firstLetterUpperCase(label);
    }
    if (key === 'new') return this.translate.instant('core.utils.state-levels.new');
    return this.translate.instant(`core.utils.state-levels.${key}`);
  }

  public get levelsNames$(): Observable<string[]> {
    const states = this.config.getSettingValue<TechnicalStatesRaw>('ASSET__TECHNICAL_STATES', {});
    if (states && Object.keys(states).length > 0) {
      const d = this.getLangDict(states);
      return of([d['critical'], d['deteriorated'], d['acceptable'], d['good'], d['new']].map(v => firstLetterUpperCase(v ?? '')));
    }
    return forkJoin([
      this.translate.get('core.utils.state-levels.critical'),
      this.translate.get('core.utils.state-levels.deteriorated'),
      this.translate.get('core.utils.state-levels.acceptable'),
      this.translate.get('core.utils.state-levels.good'),
    ]).pipe(shareReplay(1));
  }

  /**
   * Recover the associate label associate to a note [0-100]
   *
   * @param note
   */
  public getTechnicalStateLabelFromNote(note: number): string {
    let transKey: string;
    if (note <= 22) {
      transKey = _('core.utils.state-levels.critical');
    } else if (note <= 46) {
      transKey = _('core.utils.state-levels.deteriorated');
    } else if (note <= 70) {
      transKey = _('core.utils.state-levels.acceptable');
    } else if (note < 100) {
      transKey = _('core.utils.state-levels.good');
    } else if (note === 100) {
        transKey = _('core.utils.state-levels.new');
    } else {
       transKey = _('core.utils.state-levels.error');
    }
    return this.translate.instant(transKey)
  }

  /**
   * Recover the associate label associate to a note [0-100]
   *
   * @param level
   */
  public getTechnicalStateLabelFromLevel(level: number): string {
    if (level <= 4) return this.getStateLabel('critical');
    if (level <= 7) return this.getStateLabel('deteriorated');
    if (level <= 10) return this.getStateLabel('acceptable');
    if (level <= 14) return this.getStateLabel('good');
    return '';
  }

  /**
  * Recover the correct notation associate to a level [0-100]
  *
  * @param level
  */
  public getTechnicalStateNotationFromLevel(level: number): number {
    const LEVELS_MAP = {
      1: 0,
      2: 6,
      3: 14,
      4: 22,
      5: 30,
      6: 38,
      7: 46,
      8: 54,
      9: 62,
      10: 70,
      11: 78,
      12: 86,
      13: 94,
      14: 100
    };
    return LEVELS_MAP[level]
  }

  public getTechnicalStateNotationFromLevelOrNote(value: number): number {
    if (value === null || value === undefined) return null;
    const notation = this.getTechnicalStateNotationFromLevel(value);
    return notation !== undefined ? notation : value;
  }

  public getTechnicalStateOptionNoteFromNotation(note: number): number {
    if (note === 0) return 0;
    if (note <= 22) return 22;
    if (note <= 46) return 46;
    if (note <= 70) return 70;
    if (note < 100) return 94;
    if (note === 100) return 100;
    return null;
  }

  /**
   * Recover the associate label associate to a note [0-100]
   *
   * @param level
   */
  public getColorFromLevel(level: number): string {
    let color: string;
    if (level === 0) {
      color = '7F7F7F';
    } else if (level <= 4) {
      color = 'A80000';
    } else if (level <= 7) {
      color = 'F1838B';
    } else if (level <= 10) {
      color = 'DFA20B';
    } else if (level <= 13) {
      color = '4CC08E';
    } else if (level === 14) {
      color = '8FAADC';
    }

    return color;
  }
}
